import { Button, DialogContainer, Flex, Text, Tooltip, TooltipTrigger } from '@adobe/react-spectrum';
import { error } from '@react/react-spectrum/Toast/js/ToastContainer';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useIsMounted } from '@exchange-frontends/custom-hooks';
import { ContainerContext, formatCurrency, handleLicenseDialogueForLaunch } from '@exchange-frontends/utils';

import { useStore } from '../../../store';
import { UserDataContext } from '../../App';
import { AppDetailsContext } from '../AppDetails';
import AdditionalPurchaseRequiredDialog from './AdditionalPurchaseRequiredDialog';
import { HeaderButtonsContext } from './HeaderButtons';
import LicenseAgreementDialog from './LicenseAgreementDialog';
import RequiredProductsDialog from './RequiredProductsDialog';
import UnableToAcquirePluginDialog from './UnableToAcquirePluginDialog';

/**
 * @typedef {'REQUIRED_PRODUCTS' | 'ADDITIONAL_PURCHASE_REQUIRED' | 'LICENSE_AGREEMENT' | 'UNABLE_TO_ACQUIRE' | null} DialogType
 */

const TooltipButton = ({ children, shouldRenderTooltip }) => {
  return shouldRenderTooltip ? (
    <TooltipTrigger delay={0} placement="top">
      {children}
      <Tooltip variant="inspect">Requires additional purchase to unlock all features</Tooltip>
    </TooltipTrigger>
  ) : (
    children
  );
};

const AcquireAppButton = ({ type, onPress, isLoading }) => {
  const { ims } = useContext(ContainerContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isMounted = useIsMounted();
  const { profile: accessToken } = useContext(UserDataContext) ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  /** @type {ReturnType<typeof useState<DialogType>>} */
  const [shownDialog, setShownDialog] = useState();
  const [redirectingToSignIn, setRedirectingToSignIn] = useState(false);
  const [acquireAppLoading, setAcquireAppLoading] = useState(false);
  const { selectedOrg } = useContext(HeaderButtonsContext);
  const { cloud, isPreview, isUnavailableInEU } = useContext(AppDetailsContext);
  const detailScreen = useStore((state) => state.detailScreen);
  const detailScreenLoading = useStore((state) => state.detailScreenLoading);
  const { requiresPayment, requiresSignIn, pricingInfo, privacyPolicy, appType } = detailScreen ?? {};
  const subscriptionDays = pricingInfo?.subscription?.days || pricingInfo?.days;
  const price = pricingInfo?.price;
  const subscriptionTenure = subscriptionDays === 30 ? 'mo' : subscriptionDays === 365 ? 'yr' : undefined;
  const licenseAgreement = detailScreen?.licenseAgreement ?? detailScreen?.eula;

  const pluginAcquireFlow = searchParams.get('pluginAcquireFlow');
  const urlContainsBuyKeyword = location?.pathname.endsWith('/buy');
  const pathnameWorkflow = urlContainsBuyKeyword ? 'buy' : null;
  const searchParamsWorkflow = searchParams.get('workflow');
  const isAppBuilderGetButton = appType === 'ABD' && pricingInfo?.purchaseType === 'ADDITIONAL_PURCHASE_REQUIRED';

  const onPressInProgress = useRef(false);

  const validationStepHandlers = useMemo(() => {
    const handleRequiredProducts = () => {
      if (detailScreen.hostApps?.length && cloud === 'ec') {
        setShownDialog('REQUIRED_PRODUCTS');
        return false;
      }
      return true;
    };

    const handleAdditionalPurchaseRequired = () => {
      if (isAppBuilderGetButton) {
        setShownDialog('ADDITIONAL_PURCHASE_REQUIRED');
        return false;
      }
      return true;
    };

    const handleLicenseAgreement = () => {
      if (licenseAgreement) {
        setShownDialog('LICENSE_AGREEMENT');
        return false;
      }
      return true;
    };

    const handleSignIn = () => {
      if (!accessToken) {
        setRedirectingToSignIn(true);
        const pluginAcquireFlow = type === 'trial' ? 'handleTrial' : 'handleBuy';
        setSearchParams(createSearchParams({ pluginAcquireFlow }));
        ims.signIn();
        return false;
      }
      return true;
    };

    const handleCheckEnterprise = () => {
      if (cloud === 'ec' && selectedOrg?.type !== 'entp') {
        setShownDialog('UNABLE_TO_ACQUIRE');
        return false;
      }
      return true;
    };

    return {
      ADDITIONAL_PURCHASE_REQUIRED: handleAdditionalPurchaseRequired,
      REQUIRED_PRODUCTS: handleRequiredProducts,
      LICENSE_AGREEMENT: handleLicenseAgreement,
      SIGN_IN: handleSignIn,
      CHECK_ENTERPRISE: handleCheckEnterprise,
    };
  }, [
    accessToken,
    cloud,
    detailScreen.hostApps?.length,
    ims,
    licenseAgreement,
    isAppBuilderGetButton,
    selectedOrg?.type,
    setSearchParams,
    type,
  ]);

  // This function allows us to validate from a specific step to the last step.
  // If any step fails, the function will return false.
  const validateBeforeAcquirement = useCallback(
    (step) => {
      const allSteps = Object.keys(validationStepHandlers);
      const steps = allSteps.slice(allSteps.indexOf(step));
      for (const step of steps) {
        const didPass = validationStepHandlers[step]();
        if (!didPass) {
          return false;
        }
      }
      return true;
    },
    [validationStepHandlers]
  );

  const onButtonPress = useCallback(
    async (step = 'REQUIRED_PRODUCTS') => {
      if (onPressInProgress.current || !validateBeforeAcquirement(step)) {
        return;
      }
      setAcquireAppLoading(true);
      try {
        onPressInProgress.current = true;
        await onPress();
        onPressInProgress.current = false;
        if (type !== 'paid' && isMounted.current) {
          setAcquireAppLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (isMounted.current) {
          setAcquireAppLoading(false);
          error(err.userMessage, { timeout: 0 });
        }
      }
    },
    [validateBeforeAcquirement, onPress, type, isMounted]
  );

  useEffect(() => {
    setAcquireAppLoading(isLoading);
  }, [isLoading]);

  // This is to handle the case where the user is redirected from CCD or other 3rd party app to acquire the app.
  // Specifically when the pathname ends with /buy or workflow=buy is present in the search params.
  // It removes the /buy from the pathname or the workflow=buy from to the search params to avoid infinite loops.
  useEffect(() => {
    const workflow = pathnameWorkflow || searchParamsWorkflow;
    if (workflow === 'buy' && ims?.initialized) {
      if (searchParamsWorkflow) {
        searchParams.delete('workflow');
        setSearchParams(searchParams);
      }
      if (pathnameWorkflow) {
        navigate(location.pathname.replace('/buy', ''));
      }
      onButtonPress('REQUIRED_PRODUCTS');
    }
  }, [
    navigate,
    searchParams,
    onButtonPress,
    setSearchParams,
    pathnameWorkflow,
    ims?.initialized,
    location.pathname,
    searchParamsWorkflow,
  ]);

  // This is to handle the case where the user comes back from the sign-in page.
  // If the user did not sign, pluginAcquireFlow will get removed from the search params to avoid sending the user back to sign-in page.
  // If the user signed in, the user will be redirected to the next step in the acquisition flow.
  useEffect(() => {
    if (ims?.initialized && (cloud !== 'ec' || selectedOrg)) {
      const shouldAcquireTrial = pluginAcquireFlow === 'handleTrial' && type === 'trial';
      const shouldAcquireParent = pluginAcquireFlow === 'handleBuy' && type !== 'trial';
      const shouldAcquire = (shouldAcquireTrial || shouldAcquireParent) && !redirectingToSignIn && accessToken;
      const didNotSignIn = !ims?.isSignedInUser() && pluginAcquireFlow && !redirectingToSignIn;
      if (didNotSignIn || shouldAcquire) {
        searchParams.delete('pluginAcquireFlow');
        setSearchParams(searchParams);
      }
      if (shouldAcquire) {
        onButtonPress('CHECK_ENTERPRISE');
      }
    }
  }, [
    accessToken,
    onButtonPress,
    pluginAcquireFlow,
    redirectingToSignIn,
    searchParams,
    setSearchParams,
    type,
    ims,
    cloud,
    selectedOrg,
  ]);

  return (
    <>
      <DialogContainer>
        {shownDialog === 'REQUIRED_PRODUCTS' && (
          <RequiredProductsDialog
            detailScreen={detailScreen}
            onAccept={() => {
              setShownDialog(null);
              onButtonPress('LICENSE_AGREEMENT');
            }}
            close={() => {
              setShownDialog(null);
            }}
          />
        )}
        {shownDialog === 'ADDITIONAL_PURCHASE_REQUIRED' && (
          <AdditionalPurchaseRequiredDialog
            onAccept={() => {
              setShownDialog(null);
              onButtonPress('REQUIRED_PRODUCTS');
            }}
            close={() => {
              setShownDialog(null);
            }}
          />
        )}
        {shownDialog === 'LICENSE_AGREEMENT' && (
          <LicenseAgreementDialog
            cloud={cloud}
            licenseAgreement={licenseAgreement}
            privacyPolicy={privacyPolicy}
            onAccept={() => {
              setShownDialog(null);
              onButtonPress('SIGN_IN');
            }}
            close={() => {
              setShownDialog(null);
              handleLicenseDialogueForLaunch('Cancel agreement');
            }}
          />
        )}
        {shownDialog === 'UNABLE_TO_ACQUIRE' && (
          <UnableToAcquirePluginDialog
            close={() => {
              setShownDialog(null);
            }}
          />
        )}
      </DialogContainer>
      <Flex gap="size-125" alignItems="center">
        {type === 'paid' && (
          <Flex>
            {formatCurrency(price)}
            {subscriptionTenure ? '/' + subscriptionTenure : ''}
          </Flex>
        )}
        {isAppBuilderGetButton && (
          <Flex>
            <Text>Additional purchase required</Text>
          </Flex>
        )}
        <TooltipButton shouldRenderTooltip={type === 'free' && requiresSignIn && requiresPayment}>
          <Button
            variant={type === 'trial' ? 'secondary' : 'cta'}
            onPress={() => onButtonPress(isAppBuilderGetButton ? 'ADDITIONAL_PURCHASE_REQUIRED' : 'REQUIRED_PRODUCTS')}
            isDisabled={detailScreenLoading || isPreview || !ims?.initialized || isUnavailableInEU}
            data-launchid={type}
            isPending={acquireAppLoading}
            data-testid="app-details-acquire-button"
          >
            {type === 'free' && (isAppBuilderGetButton ? 'Get' : `Free${requiresSignIn && requiresPayment ? '*' : ''}`)}
            {type === 'paid' && 'Buy'}
            {type === 'trial' && 'Start Trial'}
          </Button>
        </TooltipButton>
      </Flex>
    </>
  );
};

export default AcquireAppButton;

AcquireAppButton.propTypes = {
  onPress: PropTypes.func,
  type: PropTypes.oneOf(['paid', 'free', 'trial']),
  isLoading: PropTypes.bool,
};
